import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import { userCreateFormSchema, userEditFormSchema } from '../types'
import type { UserCreateFormData, UserEditFormData } from '../types'
import { useBranchOffices } from '@/modules/clients/hooks/useBranchOffices'
import { useFormFormatter } from '@/shared/hooks/useFormFormatter'

type UserFormData = UserCreateFormData | UserEditFormData

interface UserFormProps {
  defaultValues?: Partial<UserFormData>
  onSubmit: (data: UserFormData) => void
  isSubmitting: boolean
  submitLabel?: string
  error?: string | null
  mode: 'create' | 'edit'
}

export function UserForm({
  defaultValues,
  onSubmit,
  isSubmitting,
  submitLabel = 'Guardar',
  error,
  mode,
}: UserFormProps) {
  const { branches, isLoading: branchesLoading } = useBranchOffices()
  const [showPassword, setShowPassword] = useState(false)

  const schema = mode === 'create' ? userCreateFormSchema : userEditFormSchema

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      last_name: '',
      rut: '',
      email: '',
      password: '',
      id_branch_office: undefined,
      ...defaultValues,
    },
  })

  // When branches finish loading, re-apply the default id_branch_office
  // so the <select> reflects the saved value (it may have been empty during load)
  useEffect(() => {
    if (!branchesLoading && defaultValues?.id_branch_office) {
      setValue('id_branch_office', defaultValues.id_branch_office)
    }
  }, [branchesLoading, defaultValues?.id_branch_office, setValue])

  const formatterFields = useMemo(() => [
    { name: 'rut' as const, formatter: 'rut' as const },
    { name: 'name' as const, formatter: 'name' as const },
    { name: 'last_name' as const, formatter: 'name' as const },
    { name: 'email' as const, formatter: 'email' as const },
  ], [])

  useFormFormatter<UserFormData>(watch, setValue, register, formatterFields)

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-2xl">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
          <input {...register('name')} className="input" />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Apellido *</label>
          <input {...register('last_name')} className="input" />
          {errors.last_name && (
            <p className="text-red-500 text-sm mt-1">{errors.last_name.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">RUT *</label>
          <input {...register('rut')} className="input" placeholder="12345678-9" />
          {errors.rut && <p className="text-red-500 text-sm mt-1">{errors.rut.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
          <input {...register('email')} type="email" className="input" />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {mode === 'create' ? 'Contraseña *' : 'Contraseña (dejar vacío para mantener)'}
          </label>
          <div className="relative">
            <input
              {...register('password')}
              type={showPassword ? 'text' : 'password'}
              className="input pr-10"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal *</label>
          <select
            {...register('id_branch_office', { valueAsNumber: true })}
            className="input"
            disabled={branchesLoading}
          >
            <option value="">Seleccionar sucursal</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.commune} - {branch.address}
              </option>
            ))}
          </select>
          {errors.id_branch_office && (
            <p className="text-red-500 text-sm mt-1">{errors.id_branch_office.message}</p>
          )}
        </div>
      </div>

      <div className="flex gap-3">
        <button type="submit" disabled={isSubmitting} className="btn btn-primary">
          {isSubmitting ? 'Guardando...' : submitLabel}
        </button>
      </div>
    </form>
  )
}
